import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, ShieldCheck, Check, RefreshCw, ArrowLeft } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { getPaymentRecovery, createPaymentOrder, verifyPayment } from "@/lib/api";
import Logo from "@/components/Logo";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";
const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

const loadScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function RecoverPayment() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | expired | paid | notfound | paying | done
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("notfound");
      return;
    }
    (async () => {
      try {
        const data = await getPaymentRecovery(token);
        setInfo(data);
        if (data.expired) setState("expired");
        else if (data.already_paid) setState("paid");
        else setState("ready");
      } catch {
        setState("notfound");
      }
    })();
  }, [token]);

  const handlePay = useCallback(async () => {
    setPaying(true);
    const ok = await loadScript();
    if (!ok) {
      toast.error("Couldn't load the payment window. Please try again.");
      setPaying(false);
      return;
    }
    try {
      const order = await createPaymentOrder({
        name: info.name,
        email: info.email,
        phone: info.phone,
      });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "SuperAge Health",
        description: "Precision Liver Evaluation",
        order_id: order.order_id,
        theme: { color: "#0A2E22" },
        prefill: {
          name: info.name,
          email: info.email,
          contact: info.phone,
        },
        handler: async (res) => {
          try {
            await verifyPayment({
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            setState("done");
            toast.success("Payment successful — your booking is confirmed!");
          } catch {
            toast.error("Payment couldn't be verified. Please contact us.");
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch {
      toast.error("Could not start payment. Please try again.");
    } finally {
      setPaying(false);
    }
  }, [info]);

  return (
    <div className="min-h-screen bg-sa-bg">
      <header className="border-b border-sa-beige bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="recover-home-link">
            <Logo iconClass="h-8 w-8" textClass="text-lg" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-sa-muted hover:text-sa-forest"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Home
          </Link>
        </div>
      </header>

      <main className="mx-auto flex max-w-lg flex-col items-center px-6 py-14 sm:py-20" data-testid="recover-page">
        {state === "loading" && (
          <div className="rounded-3xl border border-sa-beige bg-white p-10 text-center shadow-sm">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-sa-forest" />
            <p className="mt-3 text-sm text-sa-muted">Loading your recovery link…</p>
          </div>
        )}

        {state === "notfound" && (
          <div className="rounded-3xl border border-sa-beige bg-white p-8 text-center shadow-sm">
            <h1 className="font-display text-2xl font-medium text-sa-forest">Link not found</h1>
            <p className="mt-2 text-sm text-sa-muted">
              This recovery link is invalid or has already been used. Please contact us on WhatsApp and we'll help you complete the booking.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-sa-forest px-5 py-2.5 text-sm font-medium text-white"
            >
              Back to home
            </Link>
          </div>
        )}

        {state === "expired" && (
          <div className="rounded-3xl border border-sa-beige bg-white p-8 text-center shadow-sm">
            <h1 className="font-display text-2xl font-medium text-sa-forest">Link expired</h1>
            <p className="mt-2 text-sm text-sa-muted">
              This recovery link was valid for 14 days. Please reach out on WhatsApp and we'll send you a fresh checkout.
            </p>
            <a
              href="https://wa.me/917861917281?text=Hi%20SuperAge%20Health%2C%20my%20recovery%20link%20expired%20-%20please%20help%20me%20complete%20the%20booking"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-medium text-white"
              data-testid="recover-whatsapp"
            >
              Chat on WhatsApp
            </a>
          </div>
        )}

        {state === "paid" && (
          <div className="rounded-3xl border border-emerald-200 bg-white p-8 text-center shadow-sm" data-testid="recover-already-paid">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
              <Check className="h-7 w-7" strokeWidth={2.2} />
            </div>
            <h1 className="mt-4 font-display text-2xl font-medium text-sa-forest">You're already booked!</h1>
            <p className="mt-2 text-sm text-sa-muted">
              Your payment went through on a previous attempt. Our team will reach out within 24 hours to schedule your Precision Liver Evaluation.
            </p>
          </div>
        )}

        {state === "done" && (
          <div className="rounded-3xl border border-emerald-200 bg-white p-8 text-center shadow-sm" data-testid="recover-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
              <Check className="h-7 w-7" strokeWidth={2.2} />
            </div>
            <h1 className="mt-4 font-display text-2xl font-medium text-sa-forest">Payment successful!</h1>
            <p className="mt-2 text-sm text-sa-muted">
              A receipt is on its way to your inbox. Our care team will call you within 24 hours to schedule your evaluation.
            </p>
          </div>
        )}

        {state === "ready" && info && (
          <div className="w-full rounded-3xl border border-sa-beige bg-white p-8 shadow-sm" data-testid="recover-ready">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
              Retry payment
            </p>
            <h1 className="mt-2 font-display text-3xl font-medium leading-[1.1] text-sa-forest">
              Hi {info.name?.split(" ")[0] || "there"}, let's finish your booking
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-sa-muted">
              Your last payment didn't go through. We've saved your details — just tap Pay to complete your Precision Liver Evaluation booking.
            </p>

            <div className="mt-6 rounded-2xl bg-sa-beige/50 p-5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-sa-muted">Amount</p>
              <p className="mt-1 font-display text-3xl font-medium text-sa-forest">{inr(info.amount)}</p>
              <div className="mt-3 space-y-1 text-[13px] text-sa-forest/80">
                {info.email && <p>· {info.email}</p>}
                {info.phone && <p>· {info.phone}</p>}
              </div>
            </div>

            <button
              onClick={handlePay}
              disabled={paying}
              data-testid="recover-pay"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sa-copper px-7 py-4 text-[15px] font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.01] hover:bg-sa-copper-deep disabled:opacity-70"
            >
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Pay {inr(info.amount)} securely
            </button>
            <p className="mt-3 text-center text-[12px] text-sa-muted">
              <RefreshCw className="mr-1 inline h-3 w-3" /> Powered by Razorpay · 256-bit encrypted
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
